/**
 * GameConditions - handles condition testing and evaluation
 */

import { Condition } from '../models/Condition';
import { Check } from '../models/Check';
import { Action } from '../models/Action';
import { Finder } from '../utils/Finder';
import { Globals } from '../core/Globals';
// CommandLists compatibility removed; use Globals helper methods instead

import { Settings } from '../core/Settings';
import { isLoopCheck, CheckItemInInventory, TestCustomProperty, GetArrayIndex, varArrayCheck } from '../utils/Utilities';
import { CurrentRoomGuid } from '../utils/Constants';

declare let TheGame: any;

// Forward declarations
declare namespace GameCommands {
    function runCommandsAsync(commands: any[]): Promise<boolean>;
}
declare namespace Logger {
    function logEvaluatedCondition(condition: Condition, result: boolean): void;
}
declare function PerformTextReplacements(text: string, obj?: any): string;
declare function ResetLoopObjects(backup: boolean): void;
declare function RestoreLoopObject(): void;
declare class command {
    cmdtype: string;
    CustomChoices: any[];
}

export const GameConditions = {
    testConditionAsync: async function (tempcond: Condition, conditionAction: Action | null, loopObj: any): Promise<boolean> {
        let bResult = true;
        let counter = 0;

        if ((Settings as any).debugEnabled) {
            console.debug(tempcond);
        }

        async function performLoop(list: any[]): Promise<void> {
                const expectedLen = (Globals as any).length;
                (Globals as any).store();
                try {
                    let idx = 0;
                    let prevObject: any = null;
                    for (const obj of list) {
                        Globals.loopObject = obj;
                        // runCommandsAsync (text replacements such as [char.name],
                        // [item.name], [room.name], [exit.direction]) and advanced
                        // scripts read the current loop object from
                        // Globals.loopArgs.object. Assign a fresh object (never
                        // mutate: frames share the reference) on the frame pushed
                        // above, so nested loops restore the outer loop's object.
                        Globals.loopArgs = { object: obj, idx: idx++, array: list, prevObject };
                        prevObject = obj;
                        const shouldBreak = await (window as any).GameCommands?.runCommandsAsync?.(tempcond.PassCommands);
                        if (shouldBreak || Globals.endGame) break;
                    }
                } finally {
                    try {
                        while ((Globals as any).length > expectedLen) {
                            (Globals as any).restore();
                        }
                    } catch (e) {
                        console.error('performLoop: error restoring globals', e);
                    }
                }
                (window as any).Logger?.logEvaluatedCondition?.(tempcond, bResult);
            }

        for (let i = 0; i < tempcond.Checks.length; i++) {
            try {
                const tempcheck = tempcond.Checks[i];
                if (counter > 0) {
                    if (tempcheck.CkType == "Or") {
                        if (bResult == true)
                            break;
                    } else if (tempcheck.CkType == "And") {
                        if (bResult == false)
                            continue;
                    }
                }
                counter++;

                const step2 = (window as any).PerformTextReplacements?.(tempcheck.ConditionStep2, loopObj) || tempcheck.ConditionStep2;
                const step3 = (window as any).PerformTextReplacements?.(tempcheck.ConditionStep3, loopObj) || tempcheck.ConditionStep3;
                const step4 = (window as any).PerformTextReplacements?.(tempcheck.ConditionStep4, loopObj) || tempcheck.ConditionStep4;
                const objectBeingActedUpon = Globals.objectBeingActedUpon || Globals.theObj;

                switch (tempcheck.CondType) {
                    case "CT_Loop_While": {
                        while (this.testVariable(step2, step3, step4)) {
                            const shouldBreak = await (window as any).GameCommands?.runCommandsAsync?.(tempcond.PassCommands);
                            if (shouldBreak || Globals.endGame) break;
                        }
                        (window as any).Logger?.logEvaluatedCondition?.(tempcond, bResult);
                        break;
                    }
                    case "CT_Loop_Rooms": {
                        if (TheGame) await performLoop(TheGame.Rooms);
                        break;
                    }
                    case "CT_Loop_Exits": {
                        const temproom = Finder.room(step2);
                        if (temproom != null) {
                            await performLoop(temproom.Exits as any);
                        }
                        break;
                    }
                    case "CT_Loop_Characters": {
                        if (TheGame) await performLoop(TheGame.Characters);
                        break;
                    }
                    case "CT_Loop_Item_Group": {
                        await performLoop(Finder.objectGroup(step2) as any);
                        break;
                    }
                    case "CT_Loop_Item_Char_Inventory": {
                        if (TheGame) {
                            await performLoop(TheGame.Objects.filter((item: any) => {
                                return item.locationtype === "LT_CHARACTER" && item.locationname === step2;
                            }) as any);
                        }
                        break;
                    }
                    case "CT_Loop_Item_Container": {
                        if (TheGame) {
                            await performLoop(TheGame.Objects.filter((item: any) => {
                                return item.locationtype === "LT_IN_OBJECT" && item.locationname === step2;
                            }) as any);
                        }
                        break;
                    }
                    case "CT_Loop_Item_Inventory": {
                        if (TheGame) {
                            await performLoop(TheGame.Objects.filter((item: any) => {
                                return item.locationtype === "LT_PLAYER";
                            }) as any);
                        }
                        break;
                    }
                    case "CT_Loop_Item_Room": {
                        if (TheGame) {
                            await performLoop(TheGame.Objects.filter((item: any) => {
                                return item.locationtype === "LT_ROOM" && item.locationname === step2;
                            }) as any);
                        }
                        break;
                    }
                    case "CT_Loop_Items": {
                        if (TheGame) await performLoop(TheGame.Objects as any);
                        break;
                    }
                    default: {
                        const result = this.booleanConditionResult(tempcond, tempcheck, step2, step3, step4, objectBeingActedUpon, conditionAction);
                        // Ignore malformed conditions as RAGS does
                        if (result !== undefined) bResult = result;
                    }

                    if ((Settings as any).debugEnabled) {
                        console.debug(tempcheck.CondType, step2, step3, step4, objectBeingActedUpon, bResult);
                    }
                }
            } catch (err: any) {
                const badCheck = tempcond.Checks[i];
                (window as any).showAlert?.("Condition Error", "Rags can not process the condition check correctly.  If you are the game author, please correct the error in this conditon: " + tempcond.conditionname + " check: " +
                    badCheck.CondType + " - " + badCheck.ConditionStep2 + " - " +
                    badCheck.ConditionStep3 + " - " + badCheck.ConditionStep4);
            }
        }

        if (tempcond.Checks.length > 0 && !isLoopCheck(tempcond.Checks[0])) {
            (window as any).Logger?.logEvaluatedCondition?.(tempcond, bResult);
        }
        return bResult;
    },

    testVariable: function (step2: string, step3: string, step4: string): boolean {
        let bResult = true;
        const normalizeComparison = (comparison: string): string => {
            return comparison
                .replace(/^Hour Is\s+/i, '')
                .replace(/^Is\s+/i, '')
                .trim();
        };
        const tempvar = Finder.variable(step2);
        if (tempvar != null) {
            const varindex = GetArrayIndex(step2, 0);
            const varindex2 = GetArrayIndex(step2, 1);
            varArrayCheck(tempvar, varindex, 'testVariable');
            const replacedstring = (window as any).PerformTextReplacements?.(step4, null) || step4;
            const comparison = normalizeComparison(step3);
            let debugInfo: any = null;
            if (tempvar.vartype == "VT_DATETIMEARRAY" || tempvar.vartype == "VT_DATETIME") {
                const DateTimes = (window as any).DateTimes;
                const moment = (window as any).moment;
                const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
                const idx2 = typeof varindex2 === 'number' ? varindex2 : parseInt(String(varindex2), 10);
                let dateValue = tempvar.dtDateTime;
                if (idx != -1) {
                    if (idx2 != -1) {
                        dateValue = String(tempvar.VarArray![idx][idx2]);
                    } else {
                        dateValue = String(tempvar.VarArray![idx]);
                    }
                    // RAGS uses dtDateTime if varindex is -1, even if the type is VT_DATETIMEARRAY.
                    // Some (buggy) games depend on that behavior to work correctly.
                }

                const trimmed = (replacedstring || '').trim();
                const isNumeric = trimmed.length > 0 && !isNaN(parseFloat(trimmed));
                const hasDateChars = /[\/\-:T]/.test(trimmed) || /am|pm/i.test(trimmed);

                const parseMoment = (value: string): any => {
                    if (moment && DateTimes?.defaultDateFormat) {
                        const strict = moment(value, DateTimes.defaultDateFormat, true);
                        if (strict && strict.isValid && strict.isValid()) {
                            return strict;
                        }
                    }
                    return moment ? moment(value) : null;
                };

                const dateMoment = parseMoment(dateValue);
                if (!dateMoment || !dateMoment.isValid || !dateMoment.isValid()) {
                    return false;
                }

                if (hasDateChars) {
                    const compareMoment = parseMoment(trimmed);
                    if (!compareMoment || !compareMoment.isValid || !compareMoment.isValid()) {
                        return false;
                    }
                    const left = dateMoment.valueOf();
                    const right = compareMoment.valueOf();
                    debugInfo = {
                        mode: 'datetime',
                        dateValue,
                        compareValue: trimmed,
                        left,
                        right,
                        comparison,
                    };
                    if (comparison == "Equals") {
                        bResult = left == right;
                    } else if (comparison == "Not Equals") {
                        bResult = left != right;
                    } else if (comparison == "Greater Than") {
                        bResult = left > right;
                    } else if (comparison == "Greater Than or Equals") {
                        bResult = left >= right;
                    } else if (comparison == "Less Than") {
                        bResult = left < right;
                    } else if (comparison == "Less Than or Equals") {
                        bResult = left <= right;
                    }
                } else if (isNumeric) {
                    const hourValue = dateMoment.hour();
                    const compareValue = parseFloat(trimmed);
                    debugInfo = {
                        mode: 'hour',
                        dateValue,
                        hourValue,
                        compareValue,
                        comparison,
                    };
                    if (comparison == "Equals") {
                        bResult = hourValue == compareValue;
                    } else if (comparison == "Not Equals") {
                        bResult = hourValue != compareValue;
                    } else if (comparison == "Greater Than") {
                        bResult = hourValue > compareValue;
                    } else if (comparison == "Greater Than or Equals") {
                        bResult = hourValue >= compareValue;
                    } else if (comparison == "Less Than") {
                        bResult = hourValue < compareValue;
                    } else if (comparison == "Less Than or Equals") {
                        bResult = hourValue <= compareValue;
                    }
                } else {
                    const left = dateMoment.format(DateTimes?.defaultDateFormat || '');
                    debugInfo = {
                        mode: 'string',
                        dateValue,
                        left,
                        compareValue: trimmed,
                        comparison,
                    };
                    if (comparison == "Equals") {
                        bResult = left == trimmed;
                    } else if (comparison == "Not Equals") {
                        bResult = left != trimmed;
                    } else if (comparison == "Greater Than") {
                        bResult = left > trimmed;
                    } else if (comparison == "Less Than") {
                        bResult = left < trimmed;
                    }
                }
            } else if (tempvar.vartype == "VT_NUMBERARRAY" || tempvar.vartype == "VT_NUMBER") {
                let numberToCompare = tempvar.dNumType;
                const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
                const idx2 = typeof varindex2 === 'number' ? varindex2 : parseInt(String(varindex2), 10);
                if (idx != -1) {
                    if (idx2 != -1) {
                        numberToCompare = parseFloat(String(tempvar.VarArray![idx][idx2]));
                    } else {
                        numberToCompare = typeof tempvar.VarArray![idx] === 'number' ? tempvar.VarArray![idx] : parseFloat(String(tempvar.VarArray![idx]));
                    }
                }
                // RAGS uses dNumType if varindex is -1, even if the type is VT_NUMBERARRAY.
                // Some (buggy) games depend on that behavior to work correctly.
                if (comparison == "Equals") {
                    bResult = parseFloat(replacedstring) == numberToCompare;
                } else if (comparison == "Not Equals") {
                    bResult = parseFloat(replacedstring) != numberToCompare;
                } else if (comparison == "Greater Than") {
                    bResult = numberToCompare > parseFloat(replacedstring);
                } else if (comparison == "Greater Than or Equals") {
                    bResult = numberToCompare >= parseFloat(replacedstring);
                } else if (comparison == "Less Than") {
                    bResult = numberToCompare < parseFloat(replacedstring);
                } else if (comparison == "Less Than or Equals") {
                    bResult = numberToCompare <= parseFloat(replacedstring);
                }
                debugInfo = {
                    mode: 'number',
                    compareValue: replacedstring,
                    numberToCompare,
                    comparison,
                };
            } else if (tempvar.vartype == "VT_STRINGARRAY" || tempvar.vartype == "VT_STRING") {
                let stringToCompare = tempvar.sString;
                const idx = typeof varindex === 'number' ? varindex : parseInt(String(varindex), 10);
                const idx2 = typeof varindex2 === 'number' ? varindex2 : parseInt(String(varindex2), 10);
                if (idx != -1) {
                    if (idx2 != -1) {
                        stringToCompare = String(tempvar.VarArray![idx][idx2]);
                    } else {
                        stringToCompare = String(tempvar.VarArray![idx]);
                    }
                }
                // RAGS uses sString if varindex is -1, even if the type is VT_STRINGARRAY.
                // Some (buggy) games depend on that behavior to work correctly.
                if (comparison == "Equals") {
                    bResult = (replacedstring && replacedstring.toLowerCase()) == (stringToCompare && stringToCompare.toLowerCase());
                } else if (comparison == "Not Equals") {
                    bResult = (replacedstring && replacedstring.toLowerCase()) != (stringToCompare && stringToCompare.toLowerCase());
                } else if (comparison == "Contains") {
                    bResult = stringToCompare.toLowerCase().indexOf(replacedstring.toLowerCase()) > -1;
                } else if (comparison == "Greater Than") {
                    bResult = stringToCompare > replacedstring;
                } else if (comparison == "Less Than") {
                    bResult = stringToCompare < replacedstring;
                }
                debugInfo = {
                    mode: 'string',
                    compareValue: replacedstring,
                    stringToCompare,
                    comparison,
                };
            }
            // if (tempvar.varname && tempvar.varname.toLowerCase() === 'gametime') {
            //     console.debug('[Condition Debug] gametime comparison', {
            //         variable: tempvar.varname,
            //         vartype: tempvar.vartype,
            //         step2,
            //         step3,
            //         step4,
            //         replacedstring,
            //         result: bResult,
            //         details: debugInfo,
            //     });
            // }
        }
        return bResult;
    },

    booleanConditionResult: function (tempcond: Condition, tempcheck: Check, step2: string, step3: string, step4: string, objectBeingActedUpon: any, conditionAction: Action | null): boolean | undefined {
        switch (tempcheck.CondType) {
            case "CT_Item_InGroup": {
                const obj = Finder.object(step2);
                return obj ? obj.GroupName == step3 : false;
            }
            case "CT_MultiMedia_InGroup": {
                // The web export keeps each media item's group name but not
                // the group hierarchy, so this matches the direct group only
                // (same as CT_Item_InGroup).
                const media = (window as any).GetGameImage?.(step2);
                return media ? media.GroupName == step3 : false;
            }
            case "CT_Item_Held_By_Player": {
                const tempobj = Finder.object(step2);
                if (!tempobj) {
                    return false;
                }

                if (tempobj.locationtype == "LT_PLAYER") {
                    return true;
                } else if (tempobj.locationtype == "LT_IN_OBJECT") {
                    return CheckItemInInventory(tempobj);
                } else {
                    return false;
                }
            }
            case "CT_Player_Moving": {
                return (window as any).Globals.movingDirection == step2;
            }
            case "CT_Player_Gender": {
                if (!TheGame) return false;
                if (step2 == "Male")
                    return TheGame.Player.PlayerGender == "Male";
                else if (step2 == "Female")
                    return TheGame.Player.PlayerGender == "Female";
                else
                    return TheGame.Player.PlayerGender == "Other";
            }
            case "CT_Character_Gender": {
                const tempchar = Finder.character(step2);
                if (tempchar != null) {
                    if (step3 == "Male")
                        return tempchar.CharGender == "Male";
                    else if (step3 == "Female")
                        return tempchar.CharGender == "Female";
                    else
                        return tempchar.CharGender == "Other";
                }
                break;
            }
            case "CT_Character_In_Room": {
                const tempchar = Finder.character(step2);
                if (tempchar != null) {
                    if (step3 == CurrentRoomGuid) {
                        if (!TheGame) return false;
                        const currentroom = Finder.room(TheGame.Player.CurrentRoom);
                        return currentroom ? tempchar.CurrentRoom == currentroom.UniqueID : false;
                    } else {
                        return (tempchar.CurrentRoom == step3);
                    }
                }
                return false;
            }
            case "CT_Character_In_RoomGroup": {
                const tempchar = Finder.character(step2);
                const temproom = tempchar ? Finder.room(tempchar.CurrentRoom) : undefined;
                if (temproom != null) {
                    if ((temproom as any).Group == step3)
                        return true;
                }
                return false;
            }
            case "CT_Item_Held_By_Character": {
                const tempobj = Finder.object(step3);
                if (tempobj != null) {
                    if (tempobj.locationtype == "LT_CHARACTER" && tempobj.locationname == step2)
                        return true;
                    else
                        return false;
                } else
                    return false;
            }
            case "CT_Item_Not_Held_By_Player": {
                const tempobj = Finder.object(step2);
                if (tempobj != null)
                    return tempobj.locationtype != "LT_PLAYER";
                else
                    return false;
            }
            case "CT_Item_In_Object": {
                const tempobj = Finder.object(step2);
                if (tempobj != null)
                    return tempobj.locationtype == "LT_IN_OBJECT" && tempobj.locationname == step3;
                else
                    return false;
            }
            case "CT_Item_Not_In_Object": {
                const tempobj = Finder.object(step2);
                if (tempobj != null) {
                    const iteminobject = tempobj.locationtype == "LT_IN_OBJECT" && tempobj.locationname == step3;
                    return !iteminobject;
                } else
                    return true;
            }
            case "CT_Item_In_Room": {
                if (step3 == CurrentRoomGuid) {
                    if (!TheGame) return false;
                    const tempobj = Finder.object(step2);
                    const currentroom = Finder.room(TheGame.Player.CurrentRoom);
                    if (tempobj != null)
                        return ((tempobj.locationtype == "LT_ROOM") && (tempobj.locationname == currentroom!.UniqueID));
                    else
                        return false;
                } else {
                    const tempobj = Finder.object(step2);
                    if (tempobj != null) {
                        const room = Finder.room(step3);
                        return tempobj.locationtype == "LT_ROOM" && (
                            (tempobj.locationname === step3) || (room && tempobj.locationname === room.UniqueID)
                        );
                    } else
                        return false;
                }
            }
            case "CT_Item_In_RoomGroup": {
                const tempobj = Finder.object(step2);
                if (tempobj != null && tempobj.locationtype == "LT_ROOM") {
                    const temproom = Finder.room(tempobj.locationname);
                    if (temproom != null) {
                        if ((temproom as any).Group == step3)
                            return true;
                    }
                }
                return false;
            }
            case "CT_Player_In_Room": {
                if (!TheGame) return false;
                return Finder.room(TheGame.Player.CurrentRoom) == Finder.room(step2);
            }
            case "CT_Player_In_RoomGroup": {
                if (!TheGame) return false;
                const testroom = Finder.room(TheGame.Player.CurrentRoom);
                return testroom ? ((testroom as any).Group == step2) : false;
            }
            case "CT_Player_In_Same_Room_As": {
                if (!TheGame) return false;
                const curchar = Finder.character(step2);
                return curchar ? TheGame.Player.CurrentRoom == curchar.CurrentRoom : false;
            }
            case "CT_Room_CustomPropertyCheck": {
                const splits = step2.split(":");
                if (splits.length == 2) {
                    const roomname = splits[0];
                    const property = splits[1];
                    let temproom = null;
                    if (roomname == "<CurrentRoom>") {
                        if (TheGame) temproom = Finder.room(TheGame.Player.CurrentRoom);
                    } else {
                        temproom = Finder.room(roomname);
                    }
                    if (temproom != null) {
                        const customProp = Finder.customProp(temproom, property);
                        if (customProp) {
                            return TestCustomProperty(customProp.Value, step3, step4);
                        }
                    }
                }
                return false;
            }
            case "CT_Character_CustomPropertyCheck": {
                const splits = step2.split(":");
                if (splits.length == 2) {
                    const roomname = splits[0];
                    const property = splits[1];
                    const tempchar = Finder.character(roomname);
                    if (tempchar != null) {
                        const customProp = Finder.customProp(tempchar, property);
                        if (customProp) {
                            return TestCustomProperty(customProp.Value, step3, step4);
                        }
                    }
                }
                break;
            }
            case "CT_Timer_CustomPropertyCheck": {
                const splits = step2.split(":");
                if (splits.length == 2) {
                    const roomname = splits[0];
                    const property = splits[1];
                    const temptimer = Finder.timer(roomname);
                    if (temptimer != null) {
                        const customProp = Finder.customProp(temptimer, property);
                        if (customProp) {
                            return TestCustomProperty(customProp.Value, step3, step4);
                        }
                    }
                }
                break;
            }
            case "CT_Variable_CustomPropertyCheck": {
                const splits = step2.split(":");
                if (splits.length == 2) {
                    const roomname = splits[0];
                    const property = splits[1];
                    const tempvar = Finder.variable(roomname);
                    if (tempvar != null) {
                        const customProp = Finder.customProp(tempvar, property);
                        if (customProp) {
                            return TestCustomProperty(customProp.Value, step3, step4);
                        }
                    }
                }
                break;
            }
            case "CT_Item_CustomPropertyCheck": {
                const splits = step2.split(":");
                if (splits.length == 2) {
                    const roomname = splits[0];
                    const property = splits[1];
                    let tempobj: any = null;
                    if (roomname == "<Self>") {
                        if (objectBeingActedUpon) {
                            tempobj = objectBeingActedUpon;
                        }
                    } else {
                        tempobj = Finder.object(roomname);
                    }
                    if (tempobj != null) {
                        const customProp = Finder.customProp(tempobj, property);
                        if (customProp) {
                            return TestCustomProperty(customProp.Value, step3, step4);
                        }
                    }
                }
                break;
            }
            case "CT_Player_CustomPropertyCheck": {
                if (!TheGame) return false;
                const property = step2;
                const customProp = Finder.customProp(TheGame.Player, property);
                if (customProp) {
                    return TestCustomProperty(customProp.Value, step3, step4);
                }
                break;
            }
            case "CT_Variable_Comparison": {
                return this.testVariable(step2, step3, step4);
            }
            case "CT_Variable_To_Variable_Comparison": {
                const tempvar = Finder.variable(step2);
                const checkvar = Finder.variable(step4);
                const varindex1 = GetArrayIndex(step2, 0);
                const varindex1a = GetArrayIndex(step2, 1);
                varArrayCheck(tempvar, varindex1, 'CT_Variable_To_Variable_Comparison');
                const varindex2 = GetArrayIndex(step4, 0);
                const varindex2a = GetArrayIndex(step4, 1);
                varArrayCheck(checkvar, varindex2, 'CT_Variable_To_Variable_Comparison');
                let compareval: string | number = "";
                if (checkvar && (checkvar.vartype == "VT_NUMBERARRAY" || checkvar.vartype == "VT_NUMBER")) {
                    const idx2 = typeof varindex2 === 'number' ? varindex2 : parseInt(String(varindex2), 10);
                    const idx2a = typeof varindex2a === 'number' ? varindex2a : parseInt(String(varindex2a), 10);
                    if (idx2 == -1)
                        compareval = checkvar.dNumType.toString();
                    else {
                        if (idx2a != -1)
                            compareval = String(checkvar.VarArray![idx2][idx2a]);
                        else
                            compareval = String(checkvar.VarArray![idx2]);
                    }
                } else if (checkvar && (checkvar.vartype == "VT_STRINGARRAY" || checkvar.vartype == "VT_STRING")) {
                    const idx2 = typeof varindex2 === 'number' ? varindex2 : parseInt(String(varindex2), 10);
                    const idx2a = typeof varindex2a === 'number' ? varindex2a : parseInt(String(varindex2a), 10);
                    if (idx2 == -1)
                        compareval = checkvar.sString;
                    else {
                        if (idx2a != -1)
                            compareval = String(checkvar.VarArray![idx2][idx2a]);
                        else
                            compareval = String(checkvar.VarArray![idx2]);
                    }
                }
                if (tempvar != null) {
                    if (tempvar.vartype == "VT_NUMBERARRAY" || tempvar.vartype == "VT_NUMBER") {
                        const idx1 = typeof varindex1 === 'number' ? varindex1 : parseInt(String(varindex1), 10);
                        const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
                        if (idx1 != -1) {
                            if (idx1a != -1)
                                tempvar.dNumType = typeof tempvar.VarArray![idx1][idx1a] === 'number' ? tempvar.VarArray![idx1][idx1a] : parseFloat(String(tempvar.VarArray![idx1][idx1a]));
                            else
                                tempvar.dNumType = typeof tempvar.VarArray![idx1] === 'number' ? tempvar.VarArray![idx1] : parseFloat(String(tempvar.VarArray![idx1]));
                        }
                        const num1 = tempvar.dNumType;
                        const num2 = typeof compareval === 'number' ? compareval : parseFloat(String(compareval));
                        if (step3 == "Equals") {
                            return num1 == num2;
                        } else if (step3 == "Not Equals") {
                            return num1 != num2;
                        } else if (step3 == "Greater Than") {
                            return num1 > num2;
                        } else if (step3 == "Greater Than or Equals") {
                            return num1 >= num2;
                        } else if (step3 == "Less Than") {
                            return num1 < num2;
                        } else if (step3 == "Less Than or Equals") {
                            return num1 <= num2;
                        }
                    } else if (tempvar.vartype == "VT_STRINGARRAY" || tempvar.vartype == "VT_STRING") {
                        let tempvarvalue = tempvar.sString;
                        const idx1 = typeof varindex1 === 'number' ? varindex1 : parseInt(String(varindex1), 10);
                        const idx1a = typeof varindex1a === 'number' ? varindex1a : parseInt(String(varindex1a), 10);
                        if (idx1 != -1) {
                            if (idx1a != -1)
                                tempvarvalue = String(tempvar.VarArray![idx1][idx1a]);
                            else
                                tempvarvalue = String(tempvar.VarArray![idx1]);
                        }
                        if (step3 == "Equals") {
                            return (compareval == tempvarvalue);
                        } else if (step3 == "Not Equals") {
                            return (compareval != tempvarvalue);
                        }
                    }
                }
                break;
            }
            case "CT_Item_State_Check": {
                const tempobj = Finder.object(step2);
                if (!tempobj) {
                    return false;
                }

                if (step3 == "Open") {
                    return tempobj.bOpen == true;
                }
                if (step3 == "Closed") {
                    return tempobj.bOpen == false;
                }
                if (step3 == "Locked") {
                    return tempobj.bLocked == true;
                }
                if (step3 == "Unlocked") {
                    return tempobj.bLocked == false;
                }
                if (step3 == "Worn") {
                    return tempobj.bWorn == true;
                }
                if (step3 == "Removed") {
                    return tempobj.bWorn == false;
                }
                if (step3 == "Read") {
                    return tempobj.bRead == true;
                }
                if (step3 == "Unread") {
                    return tempobj.bRead == false;
                }
                if (step3 == "Visible") {
                    return tempobj.bVisible == true;
                }
                if (step3 == "Invisible") {
                    return tempobj.bVisible == false;
                }
                break;
            }
            case "CT_AdditionalDataCheck": {
                const datatocheck = Globals.additionalData ?? "";

                if (conditionAction && conditionAction.InputType == "Text") {
                    return step4.toLowerCase() == String(datatocheck).toLowerCase();
                } else {
                    if (typeof datatocheck === 'object' && 'UniqueIdentifier' in datatocheck) {
                        return Finder.object(step2) === Finder.object((datatocheck as any).UniqueIdentifier);
                    } else {
                        return step2.toLowerCase() == String(datatocheck).toLowerCase();
                    }
                }
            }
        }
        // Return undefined in case of malformed condition
        return undefined;
    },
};

// Make available globally
(window as any).GameConditions = GameConditions;
